// Completion progress for the header strip and the tab dots.
//
// The rules themselves live in projectCompletionValidation.ts and are NOT
// restated here. Each item below is satisfied when none of its blocker ids
// appear in that validator's output, so the strip can never claim a project is
// ready while Mark Complete still refuses it. `trackedBlockerIds` exists so a
// test can prove every blocker the validator can raise is accounted for — add a
// blocker there without adding it here and that test fails.

export type ProgressTab = 'project' | 'service' | 'ami' | 'equipment';

export interface ProgressItem {
  /** Shown in the remaining-items list. */
  label: string;
  /** Tab that owns the field, matching the nav tab ids. */
  tab: ProgressTab;
  /** Item is complete when none of these blockers are raised. */
  blockerIds: string[];
}

const SHARED_ITEMS_BEFORE: ProgressItem[] = [
  { label: 'Customer name', tab: 'project', blockerIds: ['customer_name_required'] },
  { label: 'Property address', tab: 'project', blockerIds: ['customer_address_required'] },
  // One item, two ways to fail it: absent, or not a 5-digit code.
  { label: 'ZIP code', tab: 'project', blockerIds: ['customer_zip_required', 'customer_zip_invalid'] },
  { label: 'Prepared by', tab: 'project', blockerIds: ['prepared_by_required'] },
  // One item, two ways to fail it: absent, or not a usable address.
  { label: 'Preparer email', tab: 'project', blockerIds: ['preparer_email_required', 'preparer_email_invalid'] },
  { label: 'Service rating (amps)', tab: 'service', blockerIds: ['service_rating_required'] },
  { label: 'Service voltage', tab: 'service', blockerIds: ['service_voltage_required'] },
  { label: 'Utility provider', tab: 'service', blockerIds: ['utility_provider_required'] },
];

// Satisfied when there is no equipment — proposed loads are optional. Once a
// row exists it must be complete: rating, type, source, and the preparer's own
// words behind any concurrency override.
const EQUIPMENT_ITEM: ProgressItem = {
  label: 'Equipment details',
  tab: 'equipment',
  blockerIds: [
    'equipment_rating_required',
    'equipment_type_required',
    'equipment_source_required',
    'equipment_override_reason_required',
  ],
};

// One item, two ways to fail it: no data at all, or a span under 30 days.
const MEASURED_DATA_ITEM: ProgressItem = {
  label: 'Measured demand data',
  tab: 'ami',
  blockerIds: ['ami_data_required', 'ami_insufficient_range'],
};

const TRADITIONAL_ITEMS: ProgressItem[] = [
  { label: 'Eligible occupancy & service', tab: 'ami', blockerIds: ['traditional_ineligible'] },
  { label: 'Dwelling floor area', tab: 'ami', blockerIds: ['dwelling_floor_area_required'] },
  {
    label: 'Existing load inventory',
    tab: 'ami',
    blockerIds: ['existing_load_rating_required', 'inventory_attested_required'],
  },
];

/** The measured-basis list — the default, and exactly the pre-traditional set. */
export const PROGRESS_ITEMS: ProgressItem[] = [
  ...SHARED_ITEMS_BEFORE,
  MEASURED_DATA_ITEM,
  EQUIPMENT_ITEM,
];

export const TRADITIONAL_PROGRESS_ITEMS: ProgressItem[] = [
  ...SHARED_ITEMS_BEFORE,
  ...TRADITIONAL_ITEMS,
  EQUIPMENT_ITEM,
];

/** Items for a project's report basis. Null/absent means measured — the
 * default that keeps every pre-traditional project's strip unchanged. */
export function getProgressItems(reportMethod?: string | null): ProgressItem[] {
  return reportMethod === 'traditional' ? TRADITIONAL_PROGRESS_ITEMS : PROGRESS_ITEMS;
}

export const TAB_LABELS: Record<ProgressTab, string> = {
  project: 'Project Info',
  service: 'Service Info',
  ami: 'Existing Load',
  equipment: 'New Loads',
};

/** Every blocker id the strip knows how to attribute to an item, across both
 * report bases. */
export const trackedBlockerIds = new Set(
  [...PROGRESS_ITEMS, ...TRADITIONAL_PROGRESS_ITEMS].flatMap(i => i.blockerIds)
);

export interface SectionProgress {
  tab: ProgressTab;
  label: string;
  done: number;
  total: number;
  complete: boolean;
  /** No item in this section has been satisfied yet. */
  untouched: boolean;
}

export interface ProjectProgress {
  done: number;
  total: number;
  percent: number;
  complete: boolean;
  sections: SectionProgress[];
  /** Labels of the items still outstanding, in form order. */
  remaining: ProgressItem[];
}

/** Progress from the ids of the blockers currently raised. `progressItems`
 * defaults to the measured-basis list; callers pass getProgressItems() to
 * follow the project's report basis. */
export function computeProgress(
  blockerIds: Iterable<string>,
  progressItems: ProgressItem[] = PROGRESS_ITEMS
): ProjectProgress {
  const raised = new Set(blockerIds);
  const isDone = (item: ProgressItem) => !item.blockerIds.some(id => raised.has(id));

  const sections = (Object.keys(TAB_LABELS) as ProgressTab[]).map(tab => {
    const items = progressItems.filter(i => i.tab === tab);
    const done = items.filter(isDone).length;
    return {
      tab,
      label: TAB_LABELS[tab],
      done,
      total: items.length,
      complete: done === items.length,
      untouched: done === 0,
    };
  });

  const done = progressItems.filter(isDone).length;
  const total = progressItems.length;

  return {
    done,
    total,
    percent: total === 0 ? 0 : Math.round((done / total) * 100),
    complete: done === total,
    sections,
    remaining: progressItems.filter(i => !isDone(i)),
  };
}

/** Progress for a project with nothing filled in — the pre-validation state. */
export const emptyProgress: ProjectProgress = computeProgress(trackedBlockerIds);
